import { ButtonType } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { Dropdown } from "primereact/dropdown";
import { useCallback } from "react";
import { useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type freeTextSelfCheckElementForm_EditFreeTextSelfCheckElementMutation } from "@relay/freeTextSelfCheckElementForm_EditFreeTextSelfCheckElementMutation.graphql";
import { type freeTextSelfCheckElementForm_FreeTextSelfCheckElementFragment$key } from "@relay/freeTextSelfCheckElementForm_FreeTextSelfCheckElementFragment.graphql";
import { createFreeTextSelfCheckElementFormValidationSchema } from "./free-text-self-check-element-form.consts";
import {
	EDIT_FREE_TEXT_SELF_CHECK_ELEMENT_MUTATION,
	FREE_TEXT_SELF_CHECK_ELEMENT_FRAGMENT,
} from "./free-text-self-check-element-form.graphql";
import {
	type FreeTextSelfCheckElementFormProps,
	type FreeTextSelfCheckElementFormState,
} from "./free-text-self-check-element-form.types";
import { Button, ButtonVariant } from "../../../../components/button";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { MemoizedRichTextEditor } from "../../../../components/rich-text-editor";
import { RichTextEditorToolbar } from "../../../../components/rich-text-editor/rich-text-editor.const";
import { ValidatedField } from "../../../../components/ValidatedField";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch } from "../../../../store/store.redux";
import { stripHtml } from "../../../../util/html.utils";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const FreeTextSelfCheckElementForm = ({
	freeTextSelfCheckElementFragmentRef,
	onBack,
}: FreeTextSelfCheckElementFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const setDraft = useDraftStatusUpdater();
	const element = useFragment<freeTextSelfCheckElementForm_FreeTextSelfCheckElementFragment$key>(
		FREE_TEXT_SELF_CHECK_ELEMENT_FRAGMENT,
		freeTextSelfCheckElementFragmentRef,
	);
	const [editFreeTextSelfCheckElement, isEditingFreeTextSelfCheckElement] =
		useMutation<freeTextSelfCheckElementForm_EditFreeTextSelfCheckElementMutation>(
			EDIT_FREE_TEXT_SELF_CHECK_ELEMENT_MUTATION,
		);

	const dispatch = useTypedDispatch();
	const formId = "FreeTextSelfCheckElementForm";

	const formik = useFormik<FreeTextSelfCheckElementFormState>({
		enableReinitialize: false,
		initialValues: {
			title: stripHtml(element.title),
			text: element.text ?? "",
			enableSolutionReveal: element.enableSolutionReveal,
			solutionText: element.solutionText ?? "",
		},
		validationSchema: createFreeTextSelfCheckElementFormValidationSchema(t),
		onSubmit: (values) => {
			editFreeTextSelfCheckElement({
				variables: {
					input: {
						freeTextSelfCheckElementId: element.id,
						newTitle: values.title,
						newText: values.text,
						newEnableSolutionReveal: values.enableSolutionReveal,
						newSolutionText: values.enableSolutionReveal
							? values.solutionText || null
							: null,
					},
				},
				onCompleted: () => {
					dispatch(resetArrayOfEditedForms());
				},
				updater: setDraft,
			});
		},
	});

	const handleFormEdited = useCallback(() => {
		dispatch(addEditedFormToEditedFormsArray({ form: formId }));
	}, [dispatch]);

	const enableSolutionRevealOptions = [
		{ label: t("actions.yes"), value: true },
		{ label: t("actions.no"), value: false },
	];

	return (
		<form onSubmit={formik.handleSubmit} className="p-fluid">
			<ValidatedField<FreeTextSelfCheckElementFormState, string>
				name={"title"}
				label={t("general.title")}
				component={DefaultTextFieldComponent}
				formikConfig={formik}
			/>

			<ValidatedField<FreeTextSelfCheckElementFormState, string>
				name={"text"}
				label={t("forms.text")}
				onChange={handleFormEdited}
				component={(props) => (
					<MemoizedRichTextEditor
						{...props}
						height={200}
						toolbar={RichTextEditorToolbar.WithLists}
					/>
				)}
				formikConfig={formik}
			/>

			<ValidatedField<FreeTextSelfCheckElementFormState, boolean>
				name={"enableSolutionReveal"}
				label={t("educationalOffer.freeTextSelfCheck.enableSolutionReveal")}
				onChange={handleFormEdited}
				component={({ fieldValue, updateField }) => (
					<Dropdown
						value={fieldValue}
						onChange={(e) => updateField(e.value)}
						options={enableSolutionRevealOptions}
					/>
				)}
				formikConfig={formik}
			/>

			{formik.values.enableSolutionReveal && (
				<ValidatedField<FreeTextSelfCheckElementFormState, string>
					name={"solutionText"}
					label={t("educationalOffer.freeTextSelfCheck.solutionText")}
					onChange={handleFormEdited}
					component={(props) => (
						<MemoizedRichTextEditor
							{...props}
							height={200}
							toolbar={RichTextEditorToolbar.WithLists}
						/>
					)}
					formikConfig={formik}
				/>
			)}

			<Button
				stretch
				disabled={isEditingFreeTextSelfCheckElement}
				type={ButtonType.Submit}
				label={t("actions.save")}
			/>

			<div style={{ marginTop: "1rem" }}>
				<Button
					stretch
					type={ButtonType.Button}
					variant={ButtonVariant.Strong}
					onClick={onBack}
					label={t("actions.back")}
				/>
			</div>
		</form>
	);
};
